/* Sheet (DESIGN14 §F1) — a Telegram-style bottom sheet: a rounded card that
   slides up from the bottom over a dimmed backdrop, with a grab handle, an
   optional title, a scrollable body and an optional pinned footer. Used for
   the project-name prompt, the transcript editor, the estimator feedback
   drawer, etc. Shared: F2's document tabs import it from ../components/Sheet.

   Behaviour: mounts always (so the open/close transition can play), renders
   nothing to the DOM until first opened. Escape and backdrop-tap close it;
   the body scroll is isolated. Closing is animated out before unmount of the
   inner content is irrelevant — we keep it mounted while `open` and rely on
   the CSS transition driven by the `.open` class. */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { haptic, useBackInterceptor } from '../telegram';

// Sheets share a portal host, so only the most recently opened sheet may
// receive focus or Escape. Keep the background's existing accessibility
// attributes intact when nested sheets open and close.
const activeSheets: HTMLDivElement[] = [];
const isolatedElements = new Map<HTMLElement, { inert: string | null; hidden: string | null }>();

function isolateBackground() {
  isolatedElements.forEach((previous, element) => {
    // A sheet can reopen during its exit animation. Its live open state owns
    // these attributes; a snapshot from the closing frame must not re-hide it.
    if (element.dataset.tmaSheetOpen === 'true') {
      element.removeAttribute('inert');
      element.removeAttribute('aria-hidden');
      return;
    }
    const closingSheet = element.dataset.tmaSheetOpen === 'false';
    if (closingSheet || previous.inert !== null) element.setAttribute('inert', previous.inert ?? '');
    else element.removeAttribute('inert');
    if (closingSheet) element.setAttribute('aria-hidden', 'true');
    else if (previous.hidden !== null) element.setAttribute('aria-hidden', previous.hidden);
    else element.removeAttribute('aria-hidden');
  });
  isolatedElements.clear();

  let branch: HTMLElement | undefined = activeSheets[activeSheets.length - 1];
  while (branch?.parentElement) {
    const parent: HTMLElement = branch.parentElement;
    for (const element of Array.from(parent.children)) {
      if (!(element instanceof HTMLElement) || element === branch) continue;
      isolatedElements.set(element, { inert: element.getAttribute('inert'), hidden: element.getAttribute('aria-hidden') });
      // Native inert blocks pointer, keyboard and assistive-technology access.
      // aria-hidden and the focus guard also cover older embedded WebViews.
      element.setAttribute('inert', '');
      element.setAttribute('aria-hidden', 'true');
    }
    if (parent === document.body) break;
    branch = parent;
  }
}

function canFocus(element: HTMLElement) {
  if (!element.isConnected || element.closest('[hidden], [inert], [aria-hidden="true"]') || element.matches(':disabled')) return false;
  let current: HTMLElement | null = element;
  while (current) {
    const style = window.getComputedStyle(current);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    current = current.parentElement;
  }
  return true;
}

function tabStops(dialog: HTMLElement) {
  return Array.from(dialog.querySelectorAll<HTMLElement>('a[href], button, input, select, textarea, [tabindex], [contenteditable="true"], summary'))
    .filter(element => element.tabIndex >= 0 && canFocus(element))
    .sort((a, b) => (a.tabIndex || Infinity) - (b.tabIndex || Infinity));
}

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Right-aligned header action (e.g. a text "Done"/"Save" button). */
  headerAction?: React.ReactNode;
  /** Pinned footer, above the safe-area inset — put primary actions here. */
  footer?: React.ReactNode;
  children: React.ReactNode;
  /** Extra class on the sheet card. */
  className?: string;
}

export function Sheet({
  open,
  onClose,
  title,
  headerAction,
  footer,
  children,
  className,
}: SheetProps) {
  // Telegram's native Back must dismiss the active sheet through its own
  // onClose path. That path may save a source draft or protect unsent media;
  // falling through to the project Back action would bypass both.
  useBackInterceptor(open, onClose);
  // Keep the sheet in the tree for one exit transition after `open` drops.
  const [mounted, setMounted] = useState(open);
  const closeTimer = useRef<number | null>(null);
  const anchor = useRef<HTMLSpanElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [portalHost, setPortalHost] = useState<Element | null>(null);

  // A transformed document turns position:fixed into document-relative
  // positioning. Keep every sheet in the viewport while inheriting TMA theme.
  useLayoutEffect(() => {
    setPortalHost(anchor.current?.closest('.tma-app') ?? document.body);
  }, []);

  useEffect(() => {
    if (open) {
      setMounted(true);
      if (closeTimer.current) {
        window.clearTimeout(closeTimer.current);
        closeTimer.current = null;
      }
    } else if (mounted) {
      closeTimer.current = window.setTimeout(() => setMounted(false), 240);
    }
    return () => {
      if (closeTimer.current) {
        window.clearTimeout(closeTimer.current);
        closeTimer.current = null;
      }
    };
  }, [open, mounted]);

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!open || !mounted || !portalHost || !dialog) return undefined;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    let lastFocus: HTMLElement = dialog;
    activeSheets.push(dialog);
    // Move focus off the background before hiding it from screen readers.
    // Focusing the dialog itself does not summon the phone's keyboard.
    dialog.focus({ preventScroll: true });
    isolateBackground();
    const isTop = () => activeSheets[activeSheets.length - 1] === dialog;
    const onFocus = (e: FocusEvent) => {
      if (!isTop()) return;
      if (e.target instanceof HTMLElement && dialog.contains(e.target)) lastFocus = e.target;
      else (canFocus(lastFocus) ? lastFocus : dialog).focus({ preventScroll: true });
    };
    const onKey = (e: KeyboardEvent) => {
      if (!isTop()) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        onCloseRef.current();
      } else if (e.key === 'Tab') {
        const stops = tabStops(dialog);
        const current = document.activeElement;
        if (!stops.length || current === dialog || !dialog.contains(current) || (e.shiftKey ? current === stops[0] : current === stops[stops.length - 1])) {
          e.preventDefault();
          (e.shiftKey ? stops[stops.length - 1] : stops[0])?.focus({ preventScroll: true });
          if (!stops.length) dialog.focus({ preventScroll: true });
        }
      }
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('focusin', onFocus, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('focusin', onFocus, true);
      const wasTop = isTop();
      const index = activeSheets.indexOf(dialog);
      if (index >= 0) activeSheets.splice(index, 1);
      isolateBackground();
      if (wasTop) {
        const top = activeSheets[activeSheets.length - 1];
        // React restores selection at the end of a commit. Wait until that
        // finishes so it cannot put focus back into the closing sheet.
        queueMicrotask(() => {
          if (activeSheets.includes(dialog) || activeSheets[activeSheets.length - 1] !== top) return;
          if (previousFocus && canFocus(previousFocus) && (!top || top.contains(previousFocus))) previousFocus.focus({ preventScroll: true });
          else if (top?.isConnected) top.focus({ preventScroll: true });
        });
      }
    };
  }, [open, mounted, portalHost]);

  const dismiss = () => {
    haptic.impact('light');
    onClose();
  };

  return <>
    <span ref={anchor} hidden />
    {mounted && portalHost ? createPortal(
    <div
      ref={dialogRef}
      className={'tma-sheet-root' + (open ? ' open' : '')}
      data-tma-sheet-open={open}
      role="dialog"
      aria-modal={open ? true : undefined}
      aria-hidden={!open || undefined}
      aria-label={title || 'Dialog'}
      tabIndex={-1}
      inert={!open}
    >
      <div className="tma-sheet-backdrop" onClick={dismiss} />
      <div className={'tma-sheet' + (footer ? ' has-footer' : '') + (className ? ' ' + className : '')} onClick={(e) => e.stopPropagation()}>
        <button type="button" className="tma-sheet-grab" onClick={dismiss} aria-label="Close sheet">
          <span />
        </button>
        {title || headerAction ? (
          <div className="tma-sheet-head">
            <span className="tma-sheet-title">{title}</span>
            {headerAction ? <span className="tma-sheet-action">{headerAction}</span> : null}
          </div>
        ) : null}
        <div className="tma-sheet-body">{children}</div>
        {footer ? <div className="tma-sheet-foot">{footer}</div> : null}
      </div>
    </div>, portalHost) : null}
  </>;
}

export default Sheet;
